import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { API_CONFIG } from '../config/api';
import Modal from '../components/Modal';
import './AuthCallbackPage.scss';

const AuthCallbackPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'terms' | 'silent'>('silent');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showTermsModal, setShowTermsModal] = useState<boolean>(false);
  const [termsAccepted, setTermsAccepted] = useState<boolean>(false);
  const [authData, setAuthData] = useState<{ token: string; refreshToken: string; userData: any } | null>(null);

  useEffect(() => {
    const handleAuthCallback = async () => {
      const token = searchParams.get('token');
      const refreshToken = searchParams.get('refreshToken');
      const isNewUser = searchParams.get('isNewUser') === 'true';
      const error = searchParams.get('error');

      if (error) {
        setStatus('error');
        setErrorMessage('Authentication failed. Please try again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
        return;
      }

      if (token && refreshToken) {
        try {
          // Fetch user data from the backend
          const response = await fetch(`${API_CONFIG.BASE_URL}/api/users/profile`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (!response.ok) {
            throw new Error('Failed to fetch user profile');
          }

          const userData = await response.json();

          // If new user, show terms modal first
          if (isNewUser) {
            setAuthData({ token, refreshToken, userData });
            setStatus('terms');
            setShowTermsModal(true);
          } else {
            // Existing user - login directly (silent, no loading UI shown)
            login(token, refreshToken, userData.data.user);
            // Redirect immediately to chat
            navigate('/panel/learn/');
          }
        } catch (error) {

          setStatus('error');
          setErrorMessage('Failed to complete authentication. Please try again.');
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        }
      } else {
        setStatus('error');
        setErrorMessage('Missing authentication tokens. Please try again.');
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      }
    };

    handleAuthCallback();
  }, [searchParams, navigate, login]);

  const handleAcceptTerms = () => {
    if (termsAccepted && authData) {
      setShowTermsModal(false);
      // Complete login after terms are accepted
      login(authData.token, authData.refreshToken, authData.userData.data.user);
      // Redirect immediately to chat
      navigate('/panel/learn/');
    }
  };

  // Only render if there's an error or showing terms modal
  // For successful auth, just redirect silently (no UI shown)
  if (status === 'silent') {
    return null; // Don't show any UI, let the route handle loading
  }

  return (
    <div className="auth-callback">
      {status === 'error' && (
        <div className="auth-callback__container">
          <div className="auth-callback__card">
            <div className="auth-callback__icon">
              <div className="auth-callback__error"><span className="material-symbols-outlined">close</span></div>
            </div>
            <h2 className="auth-callback__title">Authentication Error</h2>
            <p className="auth-callback__message">{errorMessage}</p>
            <button
              className="auth-callback__retry-btn"
              onClick={() => navigate('/login')}
            >
              Back to Login
            </button>
          </div>
        </div>
      )}

      {/* Terms of Service Modal for New Google Users */}
      <Modal
        isOpen={showTermsModal}
        title="Welcome to LIFE PSENsOR!"
        onClose={() => {
          setShowTermsModal(false);
          navigate('/login');
        }}
        actionLabel="Accept & Continue"
        onAction={handleAcceptTerms}
        actionDisabled={!termsAccepted}
        className="terms-modal"
      >
        <div className="terms-modal-content">
          <p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
            Please review and accept our Terms of Service to complete your registration.
          </p>
          <div className="terms-text" style={{ maxHeight: '400px', overflowY: 'auto', marginBottom: '1rem' }}>
            <h4>1. Acceptance of Terms</h4>
            <p>By accessing and using LIFE PSENsOR, you agree to be bound by these Terms of Service.</p>

            <h4>2. Medical Disclaimer</h4>
            <p><strong style={{ color: 'red' }}>NOTICE:</strong> The AI chatbots can provide general wellness knowledge and wellness advice. However, they are NOT a substitute for professional medical advice, diagnosis, or treatment.</p>

            <h4>3. User Responsibilities</h4>
            <p>Users agree to provide accurate information and use the Platform responsibly.</p>

            <h4>4. Limitation of Liability</h4>
            <p>The Platform and its operators shall not be liable for any damages arising from use of the service.</p>

            <p style={{ marginTop: '1rem' }}>
              <strong>For complete Terms of Service, please visit our website after logging in.</strong>
            </p>
          </div>

          <div className="terms-checkbox-container">
            <label className="terms-checkbox-label">
              <input
                type="checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
                className="terms-checkbox"
              />
              <span className="terms-checkbox-text">
                I have read and agree to the Terms of Service
              </span>
            </label>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default AuthCallbackPage;


